import type { OrderDetail } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { useAdminTaxInvoiceQuery } from '@/entities/tax-invoice';
import { ReissueTaxInvoiceButton } from '@/features/order/tax-invoice';
import { ContentCard, EmptyState } from '@/shared/ui';
import { TaxInvoiceBuyerDetails } from './tax-invoice-buyer-details';
import { TaxInvoiceRecordRow } from './tax-invoice-record-row';

export const OrderTaxInvoiceSection = ({
  orderId,
  order,
}: {
  readonly orderId: string;
  readonly order: OrderDetail;
}): ReactElement | null => {
  const bankTransfer =
    order.paymentProvider === 'BANK_TRANSFER' || order.payment?.provider === 'BANK_TRANSFER';
  const eligible = (order.status === 'PAID' || order.status === 'REFUNDED') && bankTransfer;
  const query = useAdminTaxInvoiceQuery(orderId, eligible);
  const records = query.data?.records ?? [];
  const hasReissuableFailure = records.some(
    (record) =>
      record.status === 'FAILED' &&
      !records.some((other) => other.kind === record.kind && other.status !== 'FAILED'),
  );
  const missingIssue =
    order.status === 'PAID' && !records.some((record) => record.kind === 'ISSUE');
  // 세금계산서는 계좌이체 주문에만 발행한다 — 카드 결제 주문에서는 칸을 두지 않는다.
  if (!bankTransfer) return null;
  let body: ReactElement;
  if (!eligible)
    body = (
      <p>
        {order.status === 'PENDING_PAYMENT' ? '입금 확인 후 발행합니다.' : '발행 대상이 아닙니다.'}
      </p>
    );
  else if (query.isPending) body = <EmptyState>불러오는 중…</EmptyState>;
  else if (!query.data) body = <EmptyState isError>세금계산서를 불러오지 못했습니다.</EmptyState>;
  else if (!query.data.request) body = <p>요청 없음</p>;
  else
    body = (
      <div className="space-y-3">
        {query.data.records.map((record) => (
          <TaxInvoiceRecordRow key={record.id} record={record} />
        ))}
        {hasReissuableFailure || missingIssue ? (
          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              {hasReissuableFailure
                ? '최근 재발행 가능한 실패 문서를 처리합니다.'
                : '유실된 발행 요청을 복구합니다.'}
            </p>
            <ReissueTaxInvoiceButton orderId={orderId} />
          </div>
        ) : null}
        <TaxInvoiceBuyerDetails request={query.data.request} />
      </div>
    );
  return <ContentCard title="세금계산서">{body}</ContentCard>;
};
